/**
 * DashboardToolbar — filter chips, search, sort, view-mode toggle.
 *
 * Extracted from DashboardPage.tsx to reduce the God Component.
 */

import {
  Search,
  LayoutTemplate,
  ShoppingBag,
  Calendar,
  Star,
  Tag,
  ArrowUpDown,
  UserCircle,
  Sparkles,
  FolderOpen,
  LayoutGrid,
  List,
  Download,
  CalendarDays,
} from "lucide-react";
import { FilterChip, StatsStrip } from "./DashboardWidgets";
import {
  SORT_OPTIONS,
  STATUS_CONFIG,
  PROJECT_TYPE_CONFIG,
  getTagColor,
  formatShortDate,
} from "./dashboardConstants";
import type {
  ViewMode,
  FilterType,
  SortKey,
} from "./dashboardConstants";
import type { FolderDef } from "../../utils/folderTagApi";
import type { TemplateMeta } from "../../utils/templateApi";
import { useNavigate } from "react-router";

/* ================================================================
   Props
   ================================================================ */

interface DashboardToolbarProps {
  stats: {
    total: number;
    brandingCount: number;
    storeCount: number;
    faceliftCount: number;
    lastUpdated: number;
    mastersCount: number;
    recentlyUpdated: number;
    masterBased: number;
  };
  // Filter state
  filterType: FilterType;
  setFilterType: (v: FilterType) => void;
  filterFavoritesOnly: boolean;
  setFilterFavoritesOnly: (v: boolean | ((p: boolean) => boolean)) => void;
  favoritesCount: number;
  selectedTags: string[];
  setSelectedTags: (v: string[] | ((p: string[]) => string[])) => void;
  allTags: string[];
  // Search
  searchQuery: string;
  setSearchQuery: (v: string) => void;
  // Sort
  sortKey: SortKey;
  setSortKey: (v: SortKey) => void;
  // Team member filter
  filterTeamMember: string;
  setFilterTeamMember: (v: string) => void;
  allTeamMembers: string[];
  // Status filter
  filterStatus: string;
  setFilterStatus: (v: string) => void;
  // Folder filter
  filterFolder: string;
  setFilterFolder: (v: string) => void;
  folders: FolderDef[];
  // Project type filter
  filterProjectType: string;
  setFilterProjectType: (v: string) => void;
  // View mode
  viewMode: ViewMode;
  setViewMode: (v: ViewMode) => void;
  // Folder/tag manager
  canEditTemplateMeta: boolean;
  onOpenFolderTagManager: () => void;
  /** All templates for CSV export */
  templates?: TemplateMeta[];
}

/* ================================================================
   Component
   ================================================================ */

export function DashboardToolbar({
  stats,
  filterType,
  setFilterType,
  filterFavoritesOnly,
  setFilterFavoritesOnly,
  favoritesCount,
  selectedTags,
  setSelectedTags,
  allTags,
  searchQuery,
  setSearchQuery,
  sortKey,
  setSortKey,
  filterTeamMember,
  setFilterTeamMember,
  allTeamMembers,
  filterStatus,
  setFilterStatus,
  filterFolder,
  setFilterFolder,
  folders,
  filterProjectType,
  setFilterProjectType,
  viewMode,
  setViewMode,
  canEditTemplateMeta,
  onOpenFolderTagManager,
  templates,
}: DashboardToolbarProps) {
  const nav = useNavigate();
  return (
    <div className="mb-6 space-y-3">
      {/* Row 0: Stats strip */}
      <StatsStrip stats={stats} />

      {/* Row 1: Filter chips + stats */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Type filter chips */}
        <div className="flex items-center gap-0.5 bg-white rounded-lg border border-gray-200/80 p-1 shadow-sm">
          <FilterChip active={filterType === "all"} onClick={() => setFilterType("all")} label={"\u05D4\u05DB\u05DC"} count={stats.total} />
          <FilterChip active={filterType === "branding"} onClick={() => setFilterType("branding")} label={"\u05EA\u05D3\u05DE\u05D9\u05EA"} count={stats.brandingCount} icon={<LayoutTemplate className="w-3 h-3" />} />
          <FilterChip active={filterType === "store"} onClick={() => setFilterType("store")} label={"\u05E1\u05D7\u05E8"} count={stats.storeCount} icon={<ShoppingBag className="w-3 h-3" />} />
          <FilterChip active={filterType === "facelift"} onClick={() => setFilterType("facelift")} label={"\u05DE\u05EA\u05D9\u05D7\u05EA \u05E4\u05E0\u05D9\u05DD"} count={stats.faceliftCount} icon={<Sparkles className="w-3 h-3" />} />
        </div>

        {/* Favorites filter */}
        {favoritesCount > 0 && (
          <span
            role="button"
            tabIndex={0}
            onClick={() => setFilterFavoritesOnly((p: boolean) => !p)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setFilterFavoritesOnly((p: boolean) => !p); }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border cursor-pointer shadow-sm ${
              filterFavoritesOnly
                ? "bg-amber-50 text-amber-700 border-amber-300"
                : "bg-white text-gray-500 border-gray-200/80 hover:bg-amber-50 hover:text-amber-600 hover:border-amber-200"
            }`}
            style={{ fontWeight: filterFavoritesOnly ? 600 : 400, transition: "background 0.15s, color 0.15s, border-color 0.15s" }}
            title={filterFavoritesOnly ? "\u05D4\u05E6\u05D2 \u05D4\u05DB\u05DC" : "\u05D4\u05E6\u05D2 \u05DE\u05D5\u05E2\u05D3\u05E4\u05D9\u05DD \u05D1\u05DC\u05D1\u05D3"}
          >
            <Star className="w-3.5 h-3.5" fill={filterFavoritesOnly ? "currentColor" : "none"} />
            {"\u05DE\u05D5\u05E2\u05D3\u05E4\u05D9\u05DD"}
            <span className={`px-1.5 py-0.5 rounded text-[10px] ${filterFavoritesOnly ? "bg-amber-200/60" : "bg-gray-100"}`}>
              {favoritesCount}
            </span>
          </span>
        )}

        {/* Last updated pill */}
        {stats.lastUpdated > 0 && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-lg border border-gray-200/80 shadow-sm">
            <Calendar className="w-3.5 h-3.5 text-emerald-500" />
            <span className="text-xs text-gray-500">{"\u05E2\u05D3\u05DB\u05D5\u05DF \u05D0\u05D7\u05E8\u05D5\u05DF:"}</span>
            <span className="text-sm text-gray-800" style={{ fontWeight: 600 }}>{formatShortDate(stats.lastUpdated)}</span>
          </div>
        )}
      </div>

      {/* Row 1.5: Tag filter chips — hidden from dashboard UI (kept in code) */}
      {false && allTags.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5 text-xs text-gray-400">
            <Tag className="w-3 h-3" />
            <span>{"\u05EA\u05D2\u05D9\u05D5\u05EA:"}</span>
          </div>
          {allTags.map((tag) => {
            const tc = getTagColor(tag);
            const isActive = selectedTags.includes(tag);
            return (
              <span
                key={tag}
                role="button"
                tabIndex={0}
                onClick={() => {
                  setSelectedTags((prev: string[]) =>
                    isActive ? prev.filter((t) => t !== tag) : [...prev, tag]
                  );
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    setSelectedTags((prev: string[]) =>
                      isActive ? prev.filter((t) => t !== tag) : [...prev, tag]
                    );
                  }
                }}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border cursor-pointer ${
                  isActive
                    ? "shadow-sm"
                    : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"
                }`}
                style={{
                  fontWeight: isActive ? 600 : 400,
                  ...(isActive ? { backgroundColor: tc.bg, color: tc.text, borderColor: tc.border } : {}),
                }}
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: tc.dot }} />
                {tag}
              </span>
            );
          })}
          {selectedTags.length > 0 && (
            <span
              role="button"
              tabIndex={0}
              onClick={() => setSelectedTags([])}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setSelectedTags([]); }}
              className="text-[11px] text-gray-400 hover:text-red-500 cursor-pointer underline"
            >
              {"\u05E0\u05E7\u05D4 \u05D4\u05DB\u05DC"}
            </span>
          )}
        </div>
      )}

      {/* Row 2: Sort + View toggle */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        {/* Sort dropdown */}
        <div className="flex items-center gap-1.5 bg-white border border-gray-200/80 rounded-lg px-2.5 py-1.5 shadow-sm">
          <ArrowUpDown className="w-3.5 h-3.5 text-gray-400 shrink-0" />
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value as SortKey)}
            className="text-xs text-gray-600 bg-transparent outline-none cursor-pointer pr-1"
            dir="rtl"
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Team member filter dropdown */}
        {allTeamMembers.length > 0 && (
          <div className="flex items-center gap-1.5 bg-white border border-gray-200/80 rounded-lg px-2.5 py-1.5 shadow-sm">
            <UserCircle className={`w-3.5 h-3.5 shrink-0 ${filterTeamMember ? "text-violet-500" : "text-gray-400"}`} />
            <select
              value={filterTeamMember}
              onChange={(e) => setFilterTeamMember(e.target.value)}
              className={`text-xs bg-transparent outline-none cursor-pointer pr-1 ${filterTeamMember ? "text-violet-700" : "text-gray-600"}`}
              style={filterTeamMember ? { fontWeight: 600 } : undefined}
              dir="rtl"
            >
              <option value="">{"\u05DB\u05DC \u05D4\u05E6\u05D5\u05D5\u05EA"}</option>
              {allTeamMembers.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Status filter dropdown */}
        <div className="flex items-center gap-1.5 bg-white border border-gray-200/80 rounded-lg px-2.5 py-1.5 shadow-sm">
          <Sparkles className={`w-3.5 h-3.5 shrink-0 ${filterStatus !== "all" ? "text-emerald-500" : "text-gray-400"}`} />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className={`text-xs bg-transparent outline-none cursor-pointer pr-1 ${filterStatus !== "all" ? "text-emerald-700" : "text-gray-600"}`}
            style={filterStatus !== "all" ? { fontWeight: 600 } : undefined}
            dir="rtl"
          >
            <option value="all">{"\u05DB\u05DC \u05D4\u05E9\u05DC\u05D1\u05D9\u05DD"}</option>
            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
              <option key={key} value={key}>{cfg.label}</option>
            ))}
          </select>
        </div>

        {/* Folder filter dropdown */}
        {folders.length > 0 && (
          <div className="flex items-center gap-1.5 bg-white border border-gray-200/80 rounded-lg px-2.5 py-1.5 shadow-sm">
            <FolderOpen className={`w-3.5 h-3.5 shrink-0 ${filterFolder !== "all" ? "text-blue-500" : "text-gray-400"}`} />
            <select
              value={filterFolder}
              onChange={(e) => setFilterFolder(e.target.value)}
              className={`text-xs bg-transparent outline-none cursor-pointer pr-1 ${filterFolder !== "all" ? "text-blue-700" : "text-gray-600"}`}
              style={filterFolder !== "all" ? { fontWeight: 600 } : undefined}
              dir="rtl"
            >
              <option value="all">{"\u05DB\u05DC \u05D4\u05EA\u05D9\u05E7\u05D9\u05D5\u05EA"}</option>
              <option value="__none">{"\u05DC\u05DC\u05D0 \u05EA\u05D9\u05E7\u05D9\u05D9\u05D4"}</option>
              {folders.map((f) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Project type filter dropdown */}
        <div className="flex items-center gap-1.5 bg-white border border-gray-200/80 rounded-lg px-2.5 py-1.5 shadow-sm">
          <LayoutTemplate className={`w-3.5 h-3.5 shrink-0 ${filterProjectType !== "all" ? "text-indigo-500" : "text-gray-400"}`} />
          <select
            value={filterProjectType}
            onChange={(e) => setFilterProjectType(e.target.value)}
            className={`text-xs bg-transparent outline-none cursor-pointer pr-1 ${filterProjectType !== "all" ? "text-indigo-700" : "text-gray-600"}`}
            style={filterProjectType !== "all" ? { fontWeight: 600 } : undefined}
            dir="rtl"
          >
            <option value="all">{"\u05DB\u05DC \u05D4\u05E4\u05E8\u05D5\u05D9\u05E7\u05D8\u05D9\u05DD"}</option>
            <option value="__none">{"\u05DC\u05DC\u05D0 \u05E1\u05D5\u05D2"}</option>
            {Object.entries(PROJECT_TYPE_CONFIG).map(([key, cfg]) => (
              <option key={key} value={key}>{cfg.label}</option>
            ))}
          </select>
        </div>

        {/* Manage folders & tags button */}
        {canEditTemplateMeta && (
          <span
            role="button"
            tabIndex={0}
            onClick={onOpenFolderTagManager}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onOpenFolderTagManager(); }}
            className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white border border-gray-200/80 rounded-lg shadow-sm text-xs text-gray-500 hover:text-blue-600 hover:border-blue-200 cursor-pointer"
            style={{ transition: "color 0.15s, border-color 0.15s" }}
            title={"\u05E0\u05D9\u05D4\u05D5\u05DC \u05EA\u05D9\u05E7\u05D9\u05D5\u05EA \u05D5\u05EA\u05D2\u05D9\u05D5\u05EA"}
          >
            <FolderOpen className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{"\u05EA\u05D9\u05E7\u05D9\u05D5\u05EA \u05D5\u05EA\u05D2\u05D9\u05D5\u05EA"}</span>
          </span>
        )}

        {/* Calendar link */}
        <span
          role="button"
          tabIndex={0}
          onClick={() => nav("/calendar")}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") nav("/calendar"); }}
          className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white border border-gray-200/80 rounded-lg shadow-sm text-xs text-gray-500 hover:text-blue-600 hover:border-blue-200 cursor-pointer"
          style={{ transition: "color 0.15s, border-color 0.15s" }}
          title={"\u05DC\u05D5\u05D7 \u05D3\u05D3\u05DC\u05D9\u05D9\u05E0\u05D9\u05DD"}
        >
          <CalendarDays className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">{"\u05DC\u05D5\u05D7 \u05E9\u05E0\u05D4"}</span>
        </span>

        {/* CSV export */}
        {templates && templates.length > 0 && (
          <span
            role="button"
            tabIndex={0}
            onClick={() => {
              import("../../utils/exportReports").then(m => m.exportTemplatesCSV(templates));
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                import("../../utils/exportReports").then(m => m.exportTemplatesCSV(templates));
              }
            }}
            className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white border border-gray-200/80 rounded-lg shadow-sm text-xs text-gray-500 hover:text-emerald-600 hover:border-emerald-200 cursor-pointer"
            style={{ transition: "color 0.15s, border-color 0.15s" }}
            title={"\u05D9\u05D9\u05E6\u05D5\u05D0 CSV"}
          >
            <Download className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">CSV</span>
          </span>
        )}

        {/* View mode toggle */}
        <div className="flex items-center gap-0.5 bg-white rounded-lg border border-gray-200/80 p-0.5 shadow-sm">
          <span
            role="button"
            tabIndex={0}
            onClick={() => setViewMode("grid")}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setViewMode("grid"); }}
            className={`p-1.5 rounded-md cursor-pointer transition-all ${viewMode === "grid" ? "bg-zap text-white shadow-sm" : "text-gray-400 hover:text-gray-600 hover:bg-gray-50"}`}
            title={"\u05EA\u05E6\u05D5\u05D2\u05EA \u05DB\u05E8\u05D8\u05D9\u05E1\u05D9\u05DD"}
          >
            <LayoutGrid className="w-3.5 h-3.5" />
          </span>
          <span
            role="button"
            tabIndex={0}
            onClick={() => setViewMode("list")}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setViewMode("list"); }}
            className={`p-1.5 rounded-md cursor-pointer transition-all ${viewMode === "list" ? "bg-zap text-white shadow-sm" : "text-gray-400 hover:text-gray-600 hover:bg-gray-50"}`}
            title={"\u05EA\u05E6\u05D5\u05D2\u05EA \u05E8\u05E9\u05D9\u05DE\u05D4"}
          >
            <List className="w-3.5 h-3.5" />
          </span>
        </div>
      </div>
    </div>
  );
}